import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  initializeRecaptchaConfig,
} from 'firebase/auth';

import { getFunctions } from 'firebase/functions';
import {
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  getFirestore,
} from 'firebase/firestore';
import { isEmpty } from 'lodash';
import { firebaseConfig } from './firebase.constants';
import { tFirebaseInstance } from './firebase.types';

let firebaseInstance: tFirebaseInstance | null = null;

function initializeFirebase(): tFirebaseInstance {
  if (isEmpty(getApps())) {
    initializeApp(firebaseConfig);
  }

  const app = getApp();
  const auth = getAuth(app);
  const firestore = getFirestore(app);
  const functions = getFunctions(app);
  const googleAuthProvider = new GoogleAuthProvider();

  // Local dev only: when the emulator hosts are set, route Auth and Firestore
  // to the Firebase emulators so a local session never touches the real
  // project. Both vars are absent in every deployed env, so this is inert there.
  const authEmulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST as
    | string
    | undefined;
  const firestoreEmulatorHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST as
    | string
    | undefined;
  if (authEmulatorHost) {
    connectAuthEmulator(auth, `http://${authEmulatorHost}`, {
      disableWarnings: true,
    });
  } else {
    // SRE-3414: load the project's reCAPTCHA Enterprise config up front so
    // signInWithPassword / signUp / password reset carry a token. Without this
    // the SDK only fetches the config after the backend rejects a request with
    // MISSING_RECAPTCHA_TOKEN, which happens in ENFORCE but never in AUDIT —
    // so AUDIT would measure nothing. Failure is non-fatal: projects without
    // reCAPTCHA configured answer this endpoint with an error, and sign-in
    // still works without a token until the project is set to ENFORCE.
    initializeRecaptchaConfig(auth).catch((error: unknown) => {
      console.warn('reCAPTCHA config initialization failed', error);
    });
  }
  if (firestoreEmulatorHost) {
    const [host, port] = firestoreEmulatorHost.split(':');
    connectFirestoreEmulator(firestore, host, Number(port));
  }

  // INT-1241: do NOT call auth.setPersistence() here. This runs at
  // module-import time on every app boot, and Firebase's setPersistence
  // migrates the signed-in user out of its current store — getCurrentUser()
  // then removeCurrentUser() then rewrite. Forcing browserSessionPersistence
  // here therefore DELETED the durable credential and moved it into per-tab
  // sessionStorage, silently undoing the rememberMe choice made at sign-in
  // (User.hook.ts) and killing the session on tab close. Persistence is owned
  // by the sign-in flow; the SDK default (IndexedDB -> localStorage) is the
  // durable behaviour we want for everything else, including Google sign-in.
  enableIndexedDbPersistence(firestore);

  firebaseInstance = {
    app,
    auth,
    firestore,
    functions,
    googleAuthProvider,
  };

  return firebaseInstance;
}

function getFirebaseInstance(): tFirebaseInstance {
  if (!firebaseInstance) {
    firebaseInstance = initializeFirebase();
  }

  return firebaseInstance;
}

export const firebase = getFirebaseInstance();
